import AccountProfile from '@mui/icons-material/AccountBox';
import ManageAgents from '@mui/icons-material/HeadsetMic';
import HelpIcon from '@mui/icons-material/HelpOutline';
import QueueIcon from '@mui/icons-material/Queue';
import SettingsIcon from '@mui/icons-material/Settings';
import Teams from '@mui/icons-material/SupervisedUserCircle';
import { useEffect, useMemo, useState } from 'react';
import Joyride, {
  ACTIONS,
  CallBackProps,
  EVENTS,
  LIFECYCLE,
} from 'react-joyride';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';
import squircleShadowIcon from '../../../images/kixie-squircle-shadow.svg';
import { agentUserId } from '../../../recoil/atoms/agentUserId';
import { IUserAtom, IUserModel, user } from '../../../recoil/atoms/user';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { AddNewAgentModal } from '../../components/modals/AddNewAgentModal';
import { AddNumberModal } from '../../components/modals/AddNumberModal';
import { CadenceModal } from '../../components/modals/CadenceModal';
import { EditAgentModal } from '../../components/modals/EditAgentModal';
import { PowerlistModal } from '../../components/modals/PowerlistModal';
import { useSnackbarWithStyles } from '../../components/notifications/SnackbarProviderWithStyles';
import { QueueEditDialog } from '../../modules/Manage/pages/queue-edit-dialog/QueueEditDialog';
import { setTouring } from '../../store/onboardingSlice';
import { useDashboardTourContext } from './DashboardTourContext';

export enum StepIndex {
  MANAGER_WELCOME = 0,
  MANAGE_TAB = 1,
  ACCOUNT_SETTINGS_TAB = 2,
  BUSINESS_METRICS = 3,
  MANAGE_TEAMS_TAB = 4,
  MANAGE_AGENTS_TAB = 5,
  MANAGE_NUMBERS_TAB = 6,
  NUMBER_SETTINGS = 7,
  MANAGE_QUEUES_TAB = 8,
  REPORTING_QUEUES_TAB = 9,
  MANAGER_COMPLETE = 10,
  MANAGE_AGENTS = 0,
  MA_NAME_COLUMN = 1,
  MA_SORT = 2,
  MA_ADD = 3,
  MA_MODAL = 4,
  MA_MODAL_TABS = 5,
  EDIT_PROFILE = 0,
  EP_PROFILE = 1,
  EP_USER_HOURS = 2,
  EP_MANAGE = 3,
  EP_EDIT = 4,
  EP_TABS = 5,
  MANAGE_NUMBERS = 0,
  NUMBERS_TABLE = 1,
  NUMBERS_SEARCH = 2,
  NUMBERS_ADD = 3,
  NUMBERS_MODAL = 4,
  NUMBERS_SEARCH_BTN = 5,
  MANAGE_POWERLISTS = 0,
  PL_ACTIONS = 1,
  PL_ACTIVE = 2,
  PL_NEW_PL_BTN = 3,
  PL_NEW_PL_MODAL = 4,
  PL_TABS = 5,
  MANAGE_QUEUES = 0,
  QUEUES_NAME = 1,
  QUEUES_EMAIL = 2,
  QUEUES_ACTIONS = 3,
  QUEUES_ADD = 4,
  QUEUES_MODAL = 5,
  QUEUES_TABS = 6,
  MANAGE_CADENCES = 0,
  CADENCES_NAME = 1,
  CADENCES_ACTIVE = 2,
  CADENCES_VERSION = 3,
  CADENCES_INFO = 4,
  CADENCES_ADD = 5,
  CADENCES_MODAL = 6,
  CADENCES_TABS = 7,
}

enum Paths { // eslint-disable-line no-unused-vars
  MANAGE_ACCOUNT = '/manage/account',
  MANAGE_TEAMS = '/manage/teams',
  MANAGE_AGENTS = '/manage/agents',
  MANAGE_NUMBERS_UNBLOCKED = '/manage/numbers/unblocked',
  MANAGE_QUEUES = '/manage/queues',
  CALL_REPORTING_QUEUES = '/call-reporting/queues',
  EDIT_AGENT = 'edit',
  MY_POWERLISTS = 'powerlists',
  SMS_TEMPLATE = 'sms-template-help',
  VM_DROP = 'vm-drop',
  AGENT_SNAPSHOT = 'agent-snapshot',
}

export function DashboardTour() {
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const {
    setState,
    state: { run, stepIndex, steps },
  } = useDashboardTourContext();

  const history = useHistory();

  const [, businessDataLoadable] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );

  const setRefetchToggleForNumbers = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_NUMBERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_NUMBERS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );

  const [userAtomLoadable] = useRecoilStateLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;

  const [
    businessSMSRulesLoadable,
    _,
    setRefetchToggleForSMSRules,
  ] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_SMS_BY_BUSINESS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_SMS_BY_BUSINESS,
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.reduce?.(
        (acc, item) => ({
          ...acc,
          nameLookup: {
            ...acc.nameLookup,
            [item.name]: item,
          },
        }),
        {
          nameLookup: {},
        }
      ) ?? cached.current
  );

  const businessData =
    businessDataLoadable.contents?.response?.data?.result?.[0];

  const business = businessData?.contents?.response?.data?.result?.[0];

  const hasAccountManager = !!business?.AssignedCAM;

  const dispatch = useDispatch();

  const userAtom = useRecoilValueLoadable(user);
  const loginUser =
    userAtom.contents && 'user' in userAtom.contents
      ? userAtom.contents.user
      : ({} as IUserModel);

  const [showMA, setShowMA] = useState(false);
  const handleCloseMA = () => setShowMA(false);
  const handleShowMA = () => setShowMA(true);

  const [showEA, setShowEA] = useState(false);
  const handleCloseEA = () => setShowEA(false);
  const handleShowEA = () => setShowEA(true);

  const [showNum, setShowNum] = useState(false);
  const handleCloseNum = () => setShowNum(false);
  const handleShowNum = () => setShowNum(true);

  const [showPL, setShowPL] = useState(false);
  const handleShowPL = () => setShowPL(true);
  const handleClosePL = () => setShowPL(false);

  const powerlistReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_BUSINESS_POWERLISTS}`,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'v1/getBusinessPowerlists',
      }),
    })
  );
  const powerlists = useMemo(
    () =>
      powerlistReportLoadable.state === 'hasValue'
        ? powerlistReportLoadable?.contents?.response?.data?.powerlists ?? []
        : [],
    [powerlistReportLoadable]
  );

  const [showQueue, setShowQueue] = useState(false);
  const handleShowQueue = () => setShowQueue(true);
  const handleCloseQueue = () => setShowQueue(false);

  const [showCadence, setShowCadence] = useState(false);
  const handleShowCadence = () => setShowCadence(true);
  const handleCloseCadence = () => setShowCadence(false);

  const onboardingSelector = useSelector((state) => state.onboarding);
  const { touring: managerTour } = onboardingSelector;

  const currentPage = window.location.pathname;
  const manageAgentsTour = currentPage === '/manage/agents';
  const [agentUserIdAtom] = useRecoilStateLoadable(agentUserId);
  const editProfileTour =
    currentPage === `/my-profile/${loginUser.userid}/edit` ||
    currentPage === `/manage/agents/${agentUserIdAtom.contents.id}/edit`;
  const numbersTour = currentPage === '/manage/numbers/unblocked';
  const powerlistsTour = currentPage === '/manage/powerlists';
  const queuesTour = currentPage === '/manage/queues';
  const cadencesTour = currentPage === '/manage/cadences';

  useEffect(() => {
    if (managerTour) {
      setState({
        steps: [
          {
            // step 0
            target: '#kt_body',
            placement: 'center',
            content: (
              <div style={{ paddingTop: 50, marginBottom: '-15px' }}>
                <div
                  style={{
                    background: '#fff',
                    borderRadius: '50%',
                    margin: '-130px auto 10px',
                    maxWidth: 100,
                    padding: 10,
                  }}
                >
                  <img
                    alt="logo"
                    src={squircleShadowIcon}
                    style={{ maxWidth: 100 }}
                  />
                </div>
                <h2>Welcome to the Kixie Dashboard!</h2>
                <p
                  style={{ textAlign: 'left', paddingTop: 10, marginBottom: 7 }}
                >
                  This is the main hub of your Kixie account. Here, you can
                  manage and review agents, permissions, call routing options,
                  teams, reporting, account settings, and more..
                </p>
                <p style={{ textAlign: 'left' }}>
                  This tour covers the topics below. Click continue to start, or
                  click any section to jump ahead.
                </p>
                <ul
                  style={{
                    listStyleType: 'none',
                    textAlign: 'left',
                  }}
                >
                  <li style={{ marginBottom: 10 }}>
                    <Link
                      to={Paths.MANAGE_ACCOUNT}
                      onClick={() => {
                        setTimeout(() => {
                          setState({ run: false });
                          setState({
                            run: true,
                            stepIndex: StepIndex.ACCOUNT_SETTINGS_TAB,
                          });
                        });
                      }}
                    >
                      <span style={{ paddingRight: 10 }}>
                        <AccountProfile />
                      </span>
                      Account Profile
                    </Link>
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <Link
                      to={Paths.MANAGE_TEAMS}
                      onClick={() => {
                        setTimeout(() => {
                          setState({ run: false });
                          setState({
                            run: true,
                            stepIndex: StepIndex.MANAGE_TEAMS_TAB,
                          });
                        });
                      }}
                    >
                      <span style={{ paddingRight: 10 }}>
                        <Teams />
                      </span>
                      Teams
                    </Link>
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <Link
                      to={Paths.MANAGE_AGENTS}
                      onClick={() => {
                        setTimeout(() => {
                          setState({ run: false });
                          setState({
                            run: true,
                            stepIndex: StepIndex.MANAGE_AGENTS_TAB,
                          });
                        });
                      }}
                    >
                      <span style={{ paddingRight: 10 }}>
                        <ManageAgents />
                      </span>
                      Manage Agents
                    </Link>
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <Link
                      to={Paths.MANAGE_NUMBERS_UNBLOCKED}
                      onClick={() => {
                        setTimeout(() => {
                          setState({ run: false });
                          setState({
                            run: true,
                            stepIndex: StepIndex.MANAGE_NUMBERS_TAB,
                          });
                        });
                      }}
                    >
                      <span style={{ paddingRight: 10 }}>
                        <SettingsIcon />
                      </span>
                      Number Settings
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={Paths.MANAGE_QUEUES}
                      onClick={() => {
                        setTimeout(() => {
                          setState({ run: false });
                          setState({
                            run: true,
                            stepIndex: StepIndex.MANAGE_QUEUES_TAB,
                          });
                        });
                      }}
                    >
                      <span style={{ paddingRight: 10 }}>
                        <QueueIcon />
                      </span>
                      Queues
                    </Link>
                  </li>
                </ul>
                <div
                  style={{
                    background: '#edfaff',
                    padding: '10px 15px',
                    borderLeft: '3px solid #46B6D8',
                    margin: '30px auto 0',
                    maxWidth: '95%',
                  }}
                >
                  <p
                    style={{ textAlign: 'left', fontSize: 13, marginBottom: 0 }}
                  >
                    Click <strong>Continue</strong> to start the tour or{' '}
                    <strong>Skip tour</strong> to take the tour later. <br />
                    You can access the tour at any time by clicking the{' '}
                    <HelpIcon style={{ marginTop: -5 }} /> icon at the top right
                    of the dashboard.
                  </p>
                </div>
              </div>
            ),
            locale: {
              skip: 'Skip tour',
              next: 'Continue',
            },
            styles: {
              options: {
                width: 550,
              },
            },
          },
          {
            // step 1
            title: 'Account Configuration',
            target: '#dtStep2',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p
                  style={{
                    textAlign: 'left',
                    marginBottom: 0,
                    paddingBottom: 0,
                  }}
                >
                  The <strong>Manage</strong> section houses pages that allow
                  you to configure your account settings, add/edit agents and
                  phone numbers, and more.
                </p>
              </div>
            ),
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'right',
            spotlightPadding: 15,
            styles: {
              options: {
                width: 400,
              },
            },
          },
          {
            // step 2
            title: 'Manage Account',
            target: '.dtStepAccount',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: 0 }}>
                  Under Manage, the <strong>Account Settings</strong> page lets
                  you view and edit your account options as well as toggle
                  various agent settings.
                </p>
              </div>
            ),
            locale: {
              skip: 'Exit tour',
              next: 'Continue',
            },
            placement: 'right',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 400,
              },
            },
          },
          {
            // step 3
            title: 'Business Metrics',
            target: '.dtStepBusinessMetrics',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: 0 }}>
                  Overall metrics for your business like total calls and agents
                  are located at the top of the page.
                </p>
              </div>
            ),
            locale: {
              skip: 'Exit tour',
              next: 'Continue',
            },
            placement: 'bottom',
            spotlightPadding: 0,
            styles: {
              options: {
                width: 400,
              },
            },
          },
          {
            // step 4
            title: 'Manage Teams',
            target: '.dtStepTeams',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: 7 }}>
                  Under Manage, the <strong>Teams</strong> page lets you view,
                  add, and edit groups of users that can be applied to other
                  Kixie features like <strong>Queues</strong> and{' '}
                  <strong>Powerlists</strong>.
                </p>
              </div>
            ),
            locale: {
              skip: 'Exit tour',
              next: 'Continue',
            },
            placement: 'right',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 420,
              },
            },
          },
          {
            // step 5
            title: 'Manage Agents',
            target: '.dtStepAgents',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: 7 }}>
                  View, add, and edit your team members on the{' '}
                  <strong>Agents</strong> page. Here, you can drill down into
                  any agent and view/configure their individual settings, as
                  well as view reporting and metrics for that agent.
                </p>
              </div>
            ),
            locale: {
              skip: 'Exit tour',
              next: 'Continue',
            },
            placement: 'right',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 450,
              },
            },
          },
          {
            // step 6
            title: 'Manage Kixie Numbers',
            target: '.dtStepNumbers',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: 7 }}>
                  View all of your phone numbers on the{' '}
                  <strong>Kixie Numbers</strong> page. Here, you can change the
                  assignment of numbers between <strong>Agents</strong> as well
                  as <strong>Queues</strong>, <strong>IVRs</strong>, and more.
                </p>
              </div>
            ),
            locale: {
              skip: 'Exit tour',
              next: 'Continue',
            },
            placement: 'right',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 400,
              },
            },
          },
          {
            // step 7
            title: 'Numbers Settings',
            target: '.dtStep7',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: 0 }}>
                  Navigate to other Numbers pages to view{' '}
                  <strong>Blocked</strong>, <strong>Routing</strong>, and{' '}
                  <strong>Team SMS</strong> number associations.
                </p>
              </div>
            ),
            locale: {
              skip: 'Exit tour',
              next: 'Continue',
            },
            placement: 'bottom',
            spotlightPadding: 0,
            styles: {
              options: {
                width: 400,
              },
            },
          },
          {
            // step 8
            title: 'Queues Management',
            target: '.dtStepQueues',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: 7 }}>
                  Create and configure <strong>Queues</strong> on this page. Add
                  Teams to queues and setup options for how calls are handled to
                  ensure your customers get the attention they need.
                </p>
              </div>
            ),
            locale: {
              skip: 'Exit tour',
              next: 'Continue',
            },
            placement: 'right',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 400,
              },
            },
          },
          {
            // step 9
            title: 'Queues Reporting',
            target: '.dtStepQueuesRep',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ marginBottom: 7, textAlign: 'left' }}>
                  Under <strong>Reporting</strong>, the <strong>Queues</strong>{' '}
                  page allows you to view stats and metrics about how your
                  Queues are performing.
                </p>
              </div>
            ),
            locale: {
              skip: 'Exit tour',
              next: 'Continue',
            },
            placement: 'right',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 400,
              },
            },
          },
          {
            // step 10
            target: '#kt_body',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                {!hasAccountManager ? (
                  <>
                    <h2 style={{ marginBottom: 15 }}>Complete!</h2>
                    <p style={{ textAlign: 'left', marginBottom: 7 }}>
                      There are many more features to explore within the
                      dashboard.
                    </p>
                    <p style={{ textAlign: 'left', marginBottom: 20 }}>
                      At Kixie, we pride ourselves on our U.S. based team of
                      customer success experts. If you'd like anything to be
                      expounded upon, we are here to help.
                    </p>
                    <p style={{ textAlign: 'left', marginBottom: 15 }}>
                      If we're not available to chat on{' '}
                      <a href="https://kixie.com">our website</a>, please email
                      us at:
                      <br />
                      <span style={{ fontWeight: 'bold' }}>
                        support@kixie.com
                      </span>
                    </p>
                    <p style={{ textAlign: 'left' }}>
                      <strong>Office hours:</strong> 6AM to 5:30PM Mon-Fri PST
                    </p>
                  </>
                ) : (
                  <>
                    <h2 style={{ marginBottom: 15 }}>Meet your manager!</h2>
                    <p style={{ textAlign: 'left', marginBottom: 7 }}>
                      There are many more features to explore within the
                      dashboard.
                    </p>
                    <p style={{ textAlign: 'left', marginBottom: 20 }}>
                      At Kixie, you have your very own account manager that you
                      can go to for help. If you'd like anything to be expounded
                      upon, feel free to contact your Kixie account manager.
                    </p>
                    <p style={{ textAlign: 'left', marginBottom: 15 }}>
                      {`${business?.assignedCAMname}`}
                      <br />
                      <span style={{ color: '#ccc' }}>Account Manager</span>
                    </p>
                    <p
                      style={{ textAlign: 'left' }}
                    >{`${business?.AssignedCAM}`}</p>
                  </>
                )}
              </div>
            ),
            locale: {
              last: (
                <span style={{ textTransform: 'uppercase', padding: '0 15px' }}>
                  end tour
                </span>
              ),
            },
            placement: 'center',
            showProgress: false,
            spotlightPadding: 0,
            styles: {
              options: {
                width: 550,
              },
            },
          },
        ],
      });
    } else if (editProfileTour) {
      setState({
        steps: [
          {
            title: `Settings`,
            target: '#subheader',
            content: (
              <div>
                <p style={{ textAlign: 'left', marginBottom: -15 }}>
                  This is the 'Agent Settings' page. Here, you can view an
                  agent's profile, products, and settings.
                </p>
              </div>
            ),
            locale: {
              skip: <strong aria-label="skip">Skip tour</strong>,
              next: 'Continue',
            },
            disableBeacon: true,
            placement: 'auto',
            styles: {
              options: {
                width: 400,
              },
            },
          },
          {
            title: `${loginUser.fname}'s Profile`,
            target: '#profileSection',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p
                  style={{
                    textAlign: 'left',
                    marginBottom: 0,
                    paddingBottom: 0,
                  }}
                >
                  The Profile section shows basic details about an agent like
                  their name, number, and email address.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 15,
            styles: {
              options: {
                width: 430,
              },
            },
          },
          {
            title: `${loginUser.fname}'s User Hours`,
            target: '#userHoursSection',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  User Hours shows what hours an agent is available and what
                  action to take during outside business hours. Agent’s can have
                  custom hours or adopt the overall account business hours
                  setting.
                </p>
              </div>
            ),
            // disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 460,
              },
            },
          },
          {
            title: `${loginUser.fname}'s Products and Settings`,
            target: '#userProductsSection',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  User Products shows additional Kixie add-on features for this
                  agent. User Settings shows basic call features.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 430,
              },
            },
          },
          {
            title: 'Edit Settings',
            target: '#editAll',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Click here to edit all settings on this page.
                </p>
              </div>
            ),
            disableBeacon: false,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'left',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 360,
              },
            },
          },
          {
            title: 'Navigation',
            target: '.modalTabs',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Lastly, use these tabs to view and edit all agent settings.
                </p>
              </div>
            ),
            locale: {
              last: 'End Tour',
            },
            placement: 'right',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 370,
              },
            },
          },
        ],
      });
    } else if (manageAgentsTour) {
      setState({
        steps: [
          {
            title: 'Manage Agents',
            target: '#subheader',
            content: (
              <div>
                <p style={{ textAlign: 'left', marginBottom: -15 }}>
                  This is the Agents page. View and manage agents here.
                </p>
              </div>
            ),
            locale: {
              skip: <strong aria-label="skip">Skip tour</strong>,
              next: 'Continue',
            },
            disableBeacon: true,
            placement: 'auto',
            styles: {
              options: {
                width: 370,
              },
            },
          },
          {
            title: 'Find an Agent',
            target: 'div[data-field="fullname"]',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p
                  style={{
                    textAlign: 'left',
                    marginBottom: 0,
                    paddingBottom: 0,
                  }}
                >
                  Here, you’ll find all the agents on your account. Click an
                  agent name to visit individual reporting and controls for that
                  agent.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'right',
            spotlightPadding: 15,
            styles: {
              options: {
                width: 420,
              },
            },
          },
          {
            title: 'Agents Table',
            target: '.dtStep5',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Filter, search, and sort agents using column headers and the
                  search bar. The CSV button downloads page data.
                </p>
              </div>
            ),
            // disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 380,
              },
            },
          },
          {
            title: 'Add Agents',
            target: '.manageAgentsAdd',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Clicking here will add a new agent to your account.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'left',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 330,
              },
            },
          },
          {
            title: 'Agent Setup',
            target: '.addNewAgentModal .MuiPaper-root',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  When adding a new agent, you can choose additional Kixie
                  products, add and assign a phone number, as well as set up
                  Voicemail Drops and SMS templates.
                </p>
              </div>
            ),
            disableBeacon: false,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 420,
              },
            },
          },
          {
            title: 'Navigation',
            target: '.modalTabs',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Lastly, use these tabs to access more agent settings.
                </p>
              </div>
            ),
            locale: {
              last: 'End Tour',
            },
            placement: 'right',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 330,
              },
            },
          },
        ],
      });
    } else if (numbersTour) {
      setState({
        steps: [
          {
            title: 'Kixie Numbers',
            target: '#subheader',
            content: (
              <div>
                <p style={{ textAlign: 'left', marginBottom: -15 }}>
                  This is the Kixie Numbers page. This page displays all numbers
                  on your account and and associated agents, teams, or other
                  entities.
                </p>
              </div>
            ),
            locale: {
              skip: <strong aria-label="skip">Skip tour</strong>,
              next: 'Continue',
            },
            disableBeacon: true,
            placement: 'auto',
            styles: {
              options: {
                width: 370,
              },
            },
          },
          {
            title: 'Numbers Table',
            target: '.numbersTable',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p
                  style={{
                    textAlign: 'left',
                    marginBottom: 0,
                    paddingBottom: 0,
                  }}
                >
                  The Numbers table displays all numbers on your account with
                  sortable columns. View phone numbers along with their Type,
                  Destination, and Default Users.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 15,
            styles: {
              options: {
                width: 420,
              },
            },
          },
          {
            title: 'Search Numbers',
            target: '#searchBox',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Use the search bar to find a specific number. Search by any
                  portion of a phone number or search by type, agent, entity,
                  destination name, or status.
                </p>
              </div>
            ),
            // disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 380,
              },
            },
          },
          {
            title: 'Add Numbers',
            target: '.numbersAdd',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Clicking here will enable you to buy new numbers for your
                  account.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'left',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 330,
              },
            },
          },
          {
            title: 'Number Details',
            target: '.addNumberModal .MuiPaper-root',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Enter details about an additional number here. Allocate the
                  number for a specific agent or entity, select a country, area
                  code, add a name, and more. This is an optional step that can
                  be done later, and the information will be editable even after
                  a number is purchased.
                </p>
              </div>
            ),
            disableBeacon: false,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 420,
              },
            },
          },
          {
            title: 'Search',
            target: '.searchBtn',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Clicking search will display a list of numbers to purchase
                  along with their price. If country, area code, and/or Toll
                  Free is specified in the fields above, the numbers displayed
                  will reflect those options. When ready to purchase, click Buy
                  next to a desired number.
                </p>
              </div>
            ),
            locale: {
              last: 'End Tour',
            },
            placement: 'left',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 450,
              },
            },
          },
        ],
      });
    } else if (powerlistsTour) {
      setState({
        steps: [
          {
            title: 'Kixie PowerLists',
            target: '#subheader',
            content: (
              <div>
                <p style={{ textAlign: 'left', marginBottom: -15 }}>
                  This is the PowerLists page. Here, you’ll find all
                  organizational PowerLists on your account along with some high
                  level stats about each.
                </p>
              </div>
            ),
            locale: {
              skip: <strong aria-label="skip">Skip tour</strong>,
              next: 'Continue',
            },
            disableBeacon: true,
            placement: 'auto',
            styles: {
              options: {
                width: 370,
              },
            },
          },
          {
            title: 'Actions',
            target: '[data-field="_actions"]',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p
                  style={{
                    textAlign: 'left',
                    marginBottom: 0,
                    paddingBottom: 0,
                  }}
                >
                  Use the actions column to view contacts in a PowerList, view
                  imported spreadsheets, and edit or delete a PowerList.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 15,
            styles: {
              options: {
                width: 420,
              },
            },
          },
          {
            title: 'Active PowerLists',
            target: '[data-field="status"]',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Use the active column to toggle a given PowerList as active or
                  inactive. This is helpful for seasonal campaigns or
                  temporarily shifting focus.
                </p>
              </div>
            ),
            // disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 390,
              },
            },
          },
          {
            title: 'Create PowerLists',
            target: '[name="New Powerlist"]',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Clicking here will create a new PowerList.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'left',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 360,
              },
            },
          },
          {
            title: 'PowerList Modal',
            target: '.powerlistModal .MuiPaper-root',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  When creating a new PowerList, create a name and select
                  various options and settings to control how numbers are
                  dialed. This provides maximum flexibility when setting up how
                  various PowerLists dial contacts.
                </p>
              </div>
            ),
            disableBeacon: false,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 420,
              },
            },
          },
          {
            title: 'Navigation',
            target: '.modalTabs',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Use these tabs to view and edit additional settings for your
                  PowerList like hours of operation, associated agents, and
                  spreadsheet data.
                </p>
              </div>
            ),
            locale: {
              last: 'End Tour',
            },
            placement: 'right',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 430,
              },
            },
          },
        ],
      });
    } else if (queuesTour) {
      setState({
        steps: [
          {
            title: 'Kixie Queues',
            target: '#subheader',
            content: (
              <div>
                <p style={{ textAlign: 'left', marginBottom: -15 }}>
                  This is the Queues page. Queues allow inbound calls to be
                  placed in line to be answered by the next available agent.
                  Create and edit Queues here.
                </p>
              </div>
            ),
            locale: {
              skip: <strong aria-label="skip">Skip tour</strong>,
              next: 'Continue',
            },
            disableBeacon: true,
            placement: 'auto',
            styles: {
              options: {
                width: 370,
              },
            },
          },
          {
            title: 'Sort by Name',
            target: '[data-field="queuename"]',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p
                  style={{
                    textAlign: 'left',
                    marginBottom: 0,
                    paddingBottom: 0,
                  }}
                >
                  View Queues by name in this column. Clicking the Queue name
                  opens the details window where you can view and adjust
                  settings and agents.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 15,
            styles: {
              options: {
                width: 420,
              },
            },
          },
          {
            title: 'Associated Emails',
            target: '[data-field="email"]',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  The Email column shows which email addresses receive a new
                  email when a voicemail is left when a call goes unanswered for
                  a given Queue.
                </p>
              </div>
            ),
            // disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 400,
              },
            },
          },
          {
            title: 'Action Items',
            target: '[data-field="action"]',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Clicking the Members icon opens a window showing Teams
                  associated with the selected Queue and their current status.
                  View the call status for agents in a Queue and see whether
                  they’re available, on an active call, on Do Not Disturb, and
                  more.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'left',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 450,
              },
            },
          },
          {
            title: 'Add New Queue',
            target: '.manageQueuesAdd',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Clicking the Add button opens a window to create a new Queue.
                </p>
              </div>
            ),
            disableBeacon: false,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 360,
              },
            },
          },
          {
            title: 'Queue Modal',
            target: '.queueModal .MuiPaper-root',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  When creating a new Queue, add a name and email address to
                  receive new voicemail messages. Then, adjust other settings
                  and options based on your Queue needs.
                </p>
              </div>
            ),
            disableBeacon: false,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 420,
              },
            },
          },
          {
            title: 'Navigation',
            target: '.modalTabs',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  To finalize setting up a new Queue, use these tabs on the side
                  to edit additional options like Queue hours, greeting options,
                  and adding Teams. When finished with all settings, click Save
                  to finish.
                </p>
              </div>
            ),
            locale: {
              last: 'End Tour',
            },
            placement: 'right',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 430,
              },
            },
          },
        ],
      });
    } else if (cadencesTour) {
      setState({
        steps: [
          {
            title: 'Cadences',
            target: '#subheader',
            content: (
              <div>
                <p style={{ textAlign: 'left', marginBottom: -15 }}>
                  This is the Cadences page. Kixie’s speed-to-lead automations
                  to initiate Calls, SMS, and webhook actions. Create and edit
                  Cadences on this page.
                </p>
              </div>
            ),
            locale: {
              skip: <strong aria-label="skip">Skip tour</strong>,
              next: 'Continue',
            },
            disableBeacon: true,
            placement: 'auto',
            styles: {
              options: {
                width: 370,
              },
            },
          },
          {
            title: 'Sort by Name',
            target: '[data-field="cadenceName"]',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p
                  style={{
                    textAlign: 'left',
                    marginBottom: 0,
                    paddingBottom: 0,
                  }}
                >
                  View added Cadences in this column. Clicking the Cadence name
                  opens the details window where you can view and edit settings
                  for that Cadence.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 15,
            styles: {
              options: {
                width: 420,
              },
            },
          },
          {
            title: 'Toggle Cadences',
            target: '[data-field="cadenceIsActive"]',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Quickly turn on or off a Cadence from this page with the
                  Active toggle without needing to go into the Cadence details
                  view.
                </p>
              </div>
            ),
            // disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 400,
              },
            },
          },
          {
            title: 'Cadence Iterations',
            target: '[data-field="version"]',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  The version number updates each time a new edit is saved for a
                  Cadence. This lets you know how many changes have been made to
                  a given Cadence to adjust its effectiveness.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'left',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 450,
              },
            },
          },
          {
            title: 'Cadence Info',
            target: '[data-field="cadenceActionCount"]',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  This column displays the number of actions associated with a
                  given Cadence. Other columns show additional information at a
                  glance about the number of rules in a Cadence and the number
                  of times it ran.
                </p>
              </div>
            ),
            disableBeacon: true,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'left',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 450,
              },
            },
          },
          {
            title: 'Add New Cadence',
            target: '.newCadenceAdd',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  Clicking the New Cadence button opens a window to create a
                  Cadence.
                </p>
              </div>
            ),
            disableBeacon: false,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 360,
              },
            },
          },
          {
            title: 'Cadence Modal',
            target: '.cadenceModal .MuiPaper-root',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  When creating a new Cadence, enter required information marked
                  with an asterisk, and then update other settings based on your
                  preferences. For more information about Cadence settings,
                  visit our help center page{' '}
                  <a
                    href="https://support.kixie.com/hc/en-us/articles/1260803683709-Creating-an-Automation-Cadence"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  .
                </p>
              </div>
            ),
            disableBeacon: false,
            locale: {
              next: 'Continue',
              skip: 'Exit tour',
            },
            placement: 'auto',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 420,
              },
            },
          },
          {
            title: 'Navigation',
            target: '.modalTabs',
            content: (
              <div style={{ marginBottom: '-15px' }}>
                <p style={{ textAlign: 'left', marginBottom: -7 }}>
                  After finishing with the initial Cadence setup, set additional
                  rules using the side tabs here. When finished with all
                  settings across all tabs, click Save to finish.
                </p>
              </div>
            ),
            locale: {
              last: 'End Tour',
            },
            placement: 'right',
            spotlightPadding: 10,
            styles: {
              options: {
                width: 430,
              },
            },
          },
        ],
      });
    }
  }, [hasAccountManager, currentPage]);

  const handleCallback = (data: CallBackProps) => {
    const { action, index, lifecycle, type } = data;

    // [start] manager tour
    if (managerTour) {
      if (action === ACTIONS.CLOSE) {
        setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
        dispatch(setTouring(false));
      } else if (action === ACTIONS.SKIP) {
        setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
        dispatch(setTouring(false));
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MANAGER_WELCOME
      ) {
        setState({ run: true, stepIndex: StepIndex.MANAGE_TAB });
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.MANAGE_TAB) {
        // manage tab step
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.ACCOUNT_SETTINGS_TAB });
          history.push(Paths.MANAGE_ACCOUNT);
        } else if (action === ACTIONS.PREV && index === StepIndex.MANAGE_TAB) {
          setState({ run: true, stepIndex: StepIndex.MANAGER_WELCOME });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.ACCOUNT_SETTINGS_TAB
      ) {
        // [settings] manage tab
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.BUSINESS_METRICS });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.ACCOUNT_SETTINGS_TAB
        ) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_TAB });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.BUSINESS_METRICS
      ) {
        // [manage account] account settings page
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_TEAMS_TAB });
          history.push(Paths.MANAGE_TEAMS);
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.BUSINESS_METRICS
        ) {
          setState({ run: true, stepIndex: StepIndex.ACCOUNT_SETTINGS_TAB });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MANAGE_TEAMS_TAB
      ) {
        // [business metrics] account metrics
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_AGENTS_TAB });
          history.push(Paths.MANAGE_AGENTS);
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.MANAGE_TEAMS_TAB
        ) {
          setState({ run: true, stepIndex: StepIndex.BUSINESS_METRICS });
          history.push(Paths.MANAGE_ACCOUNT);
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MANAGE_AGENTS_TAB
      ) {
        // [manage teams] teams page
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_NUMBERS_TAB });
          history.push(Paths.MANAGE_NUMBERS_UNBLOCKED);
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.MANAGE_AGENTS_TAB
        ) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_TEAMS_TAB });
          history.push(Paths.MANAGE_TEAMS);
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MANAGE_NUMBERS_TAB
      ) {
        // [manage agents] agents page
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.NUMBER_SETTINGS });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.MANAGE_NUMBERS_TAB
        ) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_AGENTS_TAB });
          history.push(Paths.MANAGE_AGENTS);
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.NUMBER_SETTINGS
      ) {
        // [manage kixie numbers] numbers page
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_QUEUES_TAB });
          history.push(Paths.MANAGE_QUEUES);
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.NUMBER_SETTINGS
        ) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_NUMBERS_TAB });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MANAGE_QUEUES_TAB
      ) {
        // [numbers] numbers options
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.REPORTING_QUEUES_TAB });
          history.push(Paths.CALL_REPORTING_QUEUES);
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.MANAGE_QUEUES_TAB
        ) {
          setState({ run: true, stepIndex: StepIndex.NUMBER_SETTINGS });
          history.push(Paths.MANAGE_NUMBERS_UNBLOCKED);
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.REPORTING_QUEUES_TAB
      ) {
        // [queues management] manage queues page
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.MANAGER_COMPLETE });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.REPORTING_QUEUES_TAB
        ) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_QUEUES_TAB });
          history.push(Paths.MANAGE_QUEUES);
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGER_WELCOME });
          dispatch(setTouring(false));
        }
      } else if (
        action === ACTIONS.PREV &&
        index === StepIndex.MANAGER_COMPLETE
      ) {
        // [queues reporting] queues reporting page
        setState({ run: true, stepIndex: StepIndex.REPORTING_QUEUES_TAB });
        history.push(Paths.CALL_REPORTING_QUEUES);
      } else if (action === ACTIONS.RESET || lifecycle === LIFECYCLE.COMPLETE) {
        setState({
          run: false,
          stepIndex: StepIndex.MANAGER_WELCOME,
          tourActive: false,
        });
        dispatch(setTouring(false));
      }
    }
    // [start] edit profile
    else if (editProfileTour) {
      const profileSection = document.getElementById('profileSection');
      const userHoursSection = document.getElementById('userHoursSection');
      const userProductsSection = document.getElementById(
        'userProductsSection'
      );
      const editAllButton = document.getElementById('editAll');
      // rerun on CLOSE
      if (action === ACTIONS.CLOSE) {
        setState({ run: false, stepIndex: StepIndex.EDIT_PROFILE });
        // rerun on SKIP
      } else if (action === ACTIONS.SKIP) {
        setState({ run: false, stepIndex: StepIndex.EDIT_PROFILE });
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.EDIT_PROFILE
      ) {
        profileSection?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
        setState({ run: true, stepIndex: StepIndex.EP_PROFILE });
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.EP_PROFILE) {
        if (action === ACTIONS.NEXT) {
          userHoursSection?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
          });
          setState({ run: true, stepIndex: StepIndex.EP_USER_HOURS });
        } else if (action === ACTIONS.PREV && index === StepIndex.EP_PROFILE) {
          setState({ run: true, stepIndex: StepIndex.EDIT_PROFILE });
        } else {
          setState({ run: false, stepIndex: StepIndex.EDIT_PROFILE });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.EP_USER_HOURS
      ) {
        if (action === ACTIONS.NEXT) {
          userProductsSection?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
          });
          setState({ run: true, stepIndex: StepIndex.EP_MANAGE });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.EDIT_PROFILE });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.EP_USER_HOURS
        ) {
          setState({ run: true, stepIndex: StepIndex.EP_PROFILE });
        } else {
          setState({ run: false, stepIndex: StepIndex.EDIT_PROFILE });
        }
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.EP_MANAGE) {
        if (action === ACTIONS.NEXT) {
          editAllButton?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
          });
          setState({
            run: true,
            stepIndex: StepIndex.EP_EDIT,
          });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.EDIT_PROFILE });
        } else if (action === ACTIONS.PREV && index === StepIndex.EP_MANAGE) {
          userHoursSection?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
          });
          setState({ run: true, stepIndex: StepIndex.EP_USER_HOURS });
        } else {
          setState({ run: false, stepIndex: StepIndex.EDIT_PROFILE });
        }
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.EP_EDIT) {
        if (action === ACTIONS.NEXT) {
          setTimeout(() => {
            setState({ run: false });
            handleShowEA();
            setState({ run: true, stepIndex: StepIndex.EP_TABS });
          });
          setState({ run: true, stepIndex: StepIndex.EP_TABS });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.EDIT_PROFILE });
        } else if (action === ACTIONS.PREV && index === StepIndex.EP_EDIT) {
          userProductsSection?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
          });
          setState({
            run: true,
            stepIndex: StepIndex.EP_MANAGE,
          });
        } else {
          setState({ run: false, stepIndex: StepIndex.EDIT_PROFILE });
        }
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.EP_TABS) {
        if (action === ACTIONS.NEXT || action === ACTIONS.SKIP) {
          setTimeout(() => {
            setState({ run: false, stepIndex: StepIndex.EDIT_PROFILE });
            handleCloseEA();
          });
        } else if (action === ACTIONS.PREV && index === StepIndex.EP_TABS) {
          handleCloseEA();
          setState({ run: true, stepIndex: StepIndex.EP_EDIT });
        } else {
          setState({ run: false, stepIndex: StepIndex.EDIT_PROFILE });
        }
      } else if (action === ACTIONS.RESET || lifecycle === LIFECYCLE.COMPLETE) {
        setState({
          run: false,
          stepIndex: StepIndex.EDIT_PROFILE,
          tourActive: false,
        });
      }
    }
    // [start] manage agents
    else if (manageAgentsTour) {
      // rerun on CLOSE
      if (action === ACTIONS.CLOSE) {
        setState({ run: false, stepIndex: StepIndex.MANAGE_AGENTS });
        // rerun on SKIP
      } else if (action === ACTIONS.SKIP) {
        setState({ run: false, stepIndex: StepIndex.MANAGE_AGENTS });
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MANAGE_AGENTS
      ) {
        setState({ run: true, stepIndex: StepIndex.MA_NAME_COLUMN });
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MA_NAME_COLUMN
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.MA_SORT });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.MA_NAME_COLUMN
        ) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_AGENTS });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_AGENTS });
        }
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.MA_SORT) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.MA_ADD });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_AGENTS });
        } else if (action === ACTIONS.PREV && index === StepIndex.MA_SORT) {
          setState({ run: true, stepIndex: StepIndex.MA_NAME_COLUMN });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_AGENTS });
        }
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.MA_ADD) {
        if (action === ACTIONS.NEXT) {
          setTimeout(() => {
            setState({ run: false });
            handleShowMA();
            setState({
              run: true,
              stepIndex: StepIndex.MA_MODAL,
            });
          });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_AGENTS });
        } else if (action === ACTIONS.PREV && index === StepIndex.MA_ADD) {
          setState({ run: true, stepIndex: StepIndex.MA_SORT });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_AGENTS });
        }
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.MA_MODAL) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.MA_MODAL_TABS });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_AGENTS });
        } else if (action === ACTIONS.PREV && index === StepIndex.MA_MODAL) {
          setTimeout(() => {
            setState({ run: false });
            handleCloseMA();
            setState({
              run: true,
              stepIndex: StepIndex.MA_ADD,
            });
          });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_AGENTS });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MA_MODAL_TABS
      ) {
        if (action === ACTIONS.NEXT || action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_AGENTS });
          handleCloseMA();
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.MA_MODAL_TABS
        ) {
          setState({ run: true, stepIndex: StepIndex.MA_MODAL });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_AGENTS });
          handleCloseMA();
        }
      } else if (action === ACTIONS.RESET || lifecycle === LIFECYCLE.COMPLETE) {
        setState({
          run: false,
          stepIndex: StepIndex.MANAGE_AGENTS,
          tourActive: false,
        });
      }
    }
    // // [start] numbers
    else if (numbersTour) {
      // rerun on CLOSE
      if (action === ACTIONS.CLOSE) {
        setState({ run: false, stepIndex: StepIndex.MANAGE_NUMBERS });
        // rerun on SKIP
      } else if (action === ACTIONS.SKIP) {
        setState({ run: false, stepIndex: StepIndex.MANAGE_NUMBERS });
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MANAGE_NUMBERS
      ) {
        setState({ run: true, stepIndex: StepIndex.NUMBERS_TABLE });
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.NUMBERS_TABLE
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.NUMBERS_SEARCH });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.NUMBERS_TABLE
        ) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_NUMBERS });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_NUMBERS });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.NUMBERS_SEARCH
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.NUMBERS_ADD });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_NUMBERS });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.NUMBERS_SEARCH
        ) {
          setState({ run: true, stepIndex: StepIndex.NUMBERS_TABLE });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_NUMBERS });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.NUMBERS_ADD
      ) {
        if (action === ACTIONS.NEXT) {
          setTimeout(() => {
            setState({ run: false });
            handleShowNum();
            setState({
              run: true,
              stepIndex: StepIndex.NUMBERS_MODAL,
            });
          });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_NUMBERS });
        } else if (action === ACTIONS.PREV && index === StepIndex.NUMBERS_ADD) {
          setState({ run: true, stepIndex: StepIndex.NUMBERS_SEARCH });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_NUMBERS });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.NUMBERS_MODAL
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.NUMBERS_SEARCH_BTN });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_NUMBERS });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.NUMBERS_MODAL
        ) {
          setTimeout(() => {
            setState({ run: false });
            handleCloseNum();
            setState({
              run: true,
              stepIndex: StepIndex.NUMBERS_ADD,
            });
          });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_NUMBERS });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.NUMBERS_SEARCH_BTN
      ) {
        if (action === ACTIONS.NEXT || action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_NUMBERS });
          handleCloseNum();
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.NUMBERS_SEARCH_BTN
        ) {
          setState({ run: true, stepIndex: StepIndex.NUMBERS_MODAL });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_NUMBERS });
          handleCloseNum();
        }
      } else if (action === ACTIONS.RESET || lifecycle === LIFECYCLE.COMPLETE) {
        setState({
          run: false,
          stepIndex: StepIndex.MANAGE_NUMBERS,
          tourActive: false,
        });
      }
    }
    // // [start] powerlists
    else if (powerlistsTour) {
      // rerun on CLOSE
      if (action === ACTIONS.CLOSE) {
        setState({ run: false, stepIndex: StepIndex.MANAGE_POWERLISTS });
        // rerun on SKIP
      } else if (action === ACTIONS.SKIP) {
        setState({ run: false, stepIndex: StepIndex.MANAGE_POWERLISTS });
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MANAGE_POWERLISTS
      ) {
        setState({ run: true, stepIndex: StepIndex.PL_ACTIONS });
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.PL_ACTIONS) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.PL_ACTIVE });
        } else if (action === ACTIONS.PREV && index === StepIndex.PL_ACTIONS) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_POWERLISTS });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_POWERLISTS });
        }
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.PL_ACTIVE) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.PL_NEW_PL_BTN });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_POWERLISTS });
        } else if (action === ACTIONS.PREV && index === StepIndex.PL_ACTIVE) {
          setState({ run: true, stepIndex: StepIndex.PL_ACTIONS });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_POWERLISTS });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.PL_NEW_PL_BTN
      ) {
        if (action === ACTIONS.NEXT) {
          setTimeout(() => {
            setState({ run: false });
            handleShowPL();
            setState({
              run: true,
              stepIndex: StepIndex.PL_NEW_PL_MODAL,
            });
          });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_POWERLISTS });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.PL_NEW_PL_BTN
        ) {
          setState({ run: true, stepIndex: StepIndex.PL_ACTIVE });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_POWERLISTS });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.PL_NEW_PL_MODAL
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.PL_TABS });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_POWERLISTS });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.PL_NEW_PL_MODAL
        ) {
          setTimeout(() => {
            setState({ run: false });
            handleClosePL();
            setState({
              run: true,
              stepIndex: StepIndex.PL_NEW_PL_BTN,
            });
          });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_POWERLISTS });
        }
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.PL_TABS) {
        if (action === ACTIONS.NEXT || action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_POWERLISTS });
          handleClosePL();
        } else if (action === ACTIONS.PREV && index === StepIndex.PL_TABS) {
          setState({ run: true, stepIndex: StepIndex.PL_NEW_PL_MODAL });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_POWERLISTS });
          handleClosePL();
        }
      } else if (action === ACTIONS.RESET || lifecycle === LIFECYCLE.COMPLETE) {
        setState({
          run: false,
          stepIndex: StepIndex.MANAGE_POWERLISTS,
          tourActive: false,
        });
      }
    }
    // // [start] queues
    else if (queuesTour) {
      // rerun on CLOSE
      if (action === ACTIONS.CLOSE) {
        setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
        // rerun on SKIP
      } else if (action === ACTIONS.SKIP) {
        setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MANAGE_QUEUES
      ) {
        setState({ run: true, stepIndex: StepIndex.QUEUES_NAME });
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.QUEUES_NAME
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.QUEUES_EMAIL });
        } else if (action === ACTIONS.PREV && index === StepIndex.QUEUES_NAME) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_QUEUES });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.QUEUES_EMAIL
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.QUEUES_ACTIONS });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.QUEUES_EMAIL
        ) {
          setState({ run: true, stepIndex: StepIndex.QUEUES_NAME });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.QUEUES_ACTIONS
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.QUEUES_ADD });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.QUEUES_ACTIONS
        ) {
          setState({ run: true, stepIndex: StepIndex.QUEUES_EMAIL });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
        }
      } else if (type === EVENTS.STEP_AFTER && index === StepIndex.QUEUES_ADD) {
        if (action === ACTIONS.NEXT) {
          setTimeout(() => {
            setState({ run: false });
            handleShowQueue();
            setState({
              run: true,
              stepIndex: StepIndex.QUEUES_MODAL,
            });
          });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
        } else if (action === ACTIONS.PREV && index === StepIndex.QUEUES_ADD) {
          setState({ run: true, stepIndex: StepIndex.QUEUES_ACTIONS });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.QUEUES_MODAL
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.QUEUES_TABS });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.QUEUES_MODAL
        ) {
          setTimeout(() => {
            setState({ run: false });
            handleCloseQueue();
            setState({
              run: true,
              stepIndex: StepIndex.QUEUES_ADD,
            });
          });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
          handleCloseQueue();
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.QUEUES_TABS
      ) {
        if (action === ACTIONS.NEXT || action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
          handleCloseQueue();
        } else if (action === ACTIONS.PREV && index === StepIndex.QUEUES_TABS) {
          setState({ run: true, stepIndex: StepIndex.QUEUES_MODAL });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_QUEUES });
          handleCloseQueue();
        }
      } else if (action === ACTIONS.RESET || lifecycle === LIFECYCLE.COMPLETE) {
        setState({
          run: false,
          stepIndex: StepIndex.MANAGE_QUEUES,
          tourActive: false,
        });
      }
    }
    // // [start] cadences
    else if (cadencesTour) {
      // rerun on CLOSE
      if (action === ACTIONS.CLOSE) {
        setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
        // rerun on SKIP
      } else if (action === ACTIONS.SKIP) {
        setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.MANAGE_CADENCES
      ) {
        setState({ run: true, stepIndex: StepIndex.CADENCES_NAME });
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.CADENCES_NAME
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.CADENCES_ACTIVE });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.CADENCES_NAME
        ) {
          setState({ run: true, stepIndex: StepIndex.MANAGE_CADENCES });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.CADENCES_ACTIVE
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.CADENCES_VERSION });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.CADENCES_ACTIVE
        ) {
          setState({ run: true, stepIndex: StepIndex.CADENCES_NAME });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.CADENCES_VERSION
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.CADENCES_INFO });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.CADENCES_VERSION
        ) {
          setState({ run: true, stepIndex: StepIndex.CADENCES_ACTIVE });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.CADENCES_INFO
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.CADENCES_ADD });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.CADENCES_INFO
        ) {
          setState({ run: true, stepIndex: StepIndex.CADENCES_VERSION });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.CADENCES_ADD
      ) {
        if (action === ACTIONS.NEXT) {
          setTimeout(() => {
            setState({ run: false });
            handleShowCadence();
            setState({
              run: true,
              stepIndex: StepIndex.CADENCES_MODAL,
            });
          });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.CADENCES_ADD
        ) {
          setState({ run: true, stepIndex: StepIndex.CADENCES_INFO });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.CADENCES_MODAL
      ) {
        if (action === ACTIONS.NEXT) {
          setState({ run: true, stepIndex: StepIndex.CADENCES_TABS });
        } else if (action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.CADENCES_MODAL
        ) {
          setTimeout(() => {
            setState({ run: false });
            handleCloseCadence();
            setState({
              run: true,
              stepIndex: StepIndex.CADENCES_ADD,
            });
          });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
          handleCloseCadence();
        }
      } else if (
        type === EVENTS.STEP_AFTER &&
        index === StepIndex.CADENCES_TABS
      ) {
        if (action === ACTIONS.NEXT || action === ACTIONS.SKIP) {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
          handleCloseCadence();
        } else if (
          action === ACTIONS.PREV &&
          index === StepIndex.CADENCES_TABS
        ) {
          setState({ run: true, stepIndex: StepIndex.CADENCES_MODAL });
        } else {
          setState({ run: false, stepIndex: StepIndex.MANAGE_CADENCES });
          handleCloseCadence();
        }
      } else if (action === ACTIONS.RESET || lifecycle === LIFECYCLE.COMPLETE) {
        setState({
          run: false,
          stepIndex: StepIndex.MANAGE_CADENCES,
          tourActive: false,
        });
      }
    }
  };

  return (
    <>
      <Joyride
        callback={handleCallback}
        continuous
        debug={true}
        disableScrolling={true}
        hideCloseButton={true}
        run={run}
        stepIndex={stepIndex}
        showSkipButton
        steps={steps}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#0098C7',
          },
        }}
        floaterProps={{
          options: {
            preventOverflow: {
              boundariesElement: 'viewport',
            },
          },
        }}
      />
      {showMA && (
        <AddNewAgentModal
          handleClose={handleCloseMA}
          handleShow={handleShowMA}
          show={showMA}
        />
      )}
      {showEA && (
        <EditAgentModal
          onHide={handleCloseEA}
          onShow={handleShowEA}
          show={showEA}
          id=""
        />
      )}
      {showNum && (
        <AddNumberModal
          show={showNum}
          handleClose={handleCloseNum}
          setRefetchToggleForNumbers={setRefetchToggleForNumbers}
          businessSMSRulesLoadable={businessSMSRulesLoadable}
          setRefetchToggleForSMSRules={setRefetchToggleForSMSRules}
          productData="null" // TODO: null product data?
        />
      )}
      {showPL && (
        <PowerlistModal
          show={showPL}
          id={null}
          handleClose={handleClosePL}
          enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
          powerlistReportCache={{
            current: {
              powerlists,
            },
          }}
        />
      )}
      {showQueue && (
        <QueueEditDialog id="" show={showQueue} onHide={handleCloseQueue} />
      )}
      {showCadence && (
        <CadenceModal
          show={showCadence}
          id={null}
          handleClose={handleCloseCadence}
          enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        />
      )}
    </>
  );
}
